import { TUser } from "@/api/entities/user.entity";
import { TCreateCompanyUserReqDto, TCreateCompanyUserResDto } from "@/api/services/admin/company/user/dtos/create-user.dto";
import { TDeleteCompanyUserResDto } from "@/api/services/admin/company/user/dtos/delete-user.dto";
import { TGetCompanyUserListResDto } from "@/api/services/admin/company/user/dtos/get-user-list.dto";
import { TGetCompanyUserResDto } from "@/api/services/admin/company/user/dtos/get-user.dto";
import { TResendCompanyInvitationResDto } from "@/api/services/admin/company/user/dtos/resend-invitation.dto";
import { TResetCompanyPasswordResDto } from "@/api/services/admin/company/user/dtos/reset-password.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TCompanyUserForm } from "@/features/company/types/companyUserForm";
import { usePagination } from "@/hooks/usePagination";
import { TApiRes } from "@/types/api";
import { clientApi } from "@/utils/api";
import { isNumber } from "@/utils/number";
import { createUrlWithParams } from "@/utils/url";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

export const createCompanyUserApi = async (companyId: string, payload: TCreateCompanyUserReqDto): Promise<TCreateCompanyUserResDto> => {
  const rs = await clientApi.post<TCreateCompanyUserResDto>(`/companies/${companyId}/users`, payload);
  return rs.data;
};

export const deleteCompanyUserApi = async (companyId: number, userIds: number[]): Promise<TDeleteCompanyUserResDto> => {
  const rs = await clientApi.delete<TDeleteCompanyUserResDto>(`/companies/${companyId}/users`, {
    data: {
      ids: userIds,
    },
  });
  return rs.data;
};

export const fetchCompanyUserApi = async (companyId: string, userId: string): Promise<TGetCompanyUserResDto> => {
  const rs = await clientApi.get<TGetCompanyUserResDto>(`/companies/${companyId}/users/${userId}`);
  return rs.data;
};

export const useCompanyUser = () => {
  const { id, userId } = useParams();

  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_DETAIL, id, userId],
    queryFn: () => (id && userId ? fetchCompanyUserApi(id, userId) : undefined),
    enabled: Boolean(id && isNumber(id) && userId && isNumber(userId)),
  });
};

export const fetchCompanyUserListDetailApi = async (companyId: string, params?: Record<string, unknown>): Promise<TGetCompanyUserListResDto> => {
  const url = createUrlWithParams(`/companies/${companyId}/users`, { sorts: ["createdAt|ASC"], ...params });
  const rs = await clientApi.get<TGetCompanyUserListResDto>(url);
  return rs.data;
};

export const useCompanyUserListDetail = (isUser?: boolean | null) => {
  const { id } = useParams();
  const pagReq = usePagination();
  const params = {
    ...pagReq,
    ...(isUser !== undefined && isUser !== null && { isUser }),
  };

  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_USER_LIST, id, params],
    queryFn: () => (id ? fetchCompanyUserListDetailApi(id, params) : undefined),
    enabled: Boolean(id),
    gcTime: 0,
    staleTime: 0,
  });
};

export const updateCompanyUserApi = async (companyId: string, userId: string, payload: TCompanyUserForm): Promise<TApiRes<TUser>> => {
  try {
    const { ...rest } = payload;

    const { data } = await clientApi.put<TApiRes<TUser>>(`/companies/${companyId}/users/${userId}`, {
      ...rest,
      isUser: Boolean(payload.isUser),
    });
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const resendCompanyInvitationApi = async (companyId: string, userId: string): Promise<TResendCompanyInvitationResDto> => {
  const rs = await clientApi.post<TResendCompanyInvitationResDto>(`/companies/${companyId}/users/${userId}/resend-invitation`);
  return rs.data;
};

export const resetCompanyPasswordApi = async (companyId: string, userId: string): Promise<TResetCompanyPasswordResDto> => {
  const rs = await clientApi.post<TResetCompanyPasswordResDto>(`/companies/${companyId}/users/${userId}/resend-reset-password`);
  return rs.data;
};
